import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
  type?: 'speaking' | 'mentorship' | 'general';
}

const ContactModal: React.FC<ContactModalProps> = ({ open, onClose, type = 'speaking' }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          message: formData.message,
          submission_type: type,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset form
        setFormData({ fullName: '', email: '', message: '' });
        setSuccess(false);
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          maxWidth: '800px',
          backgroundColor: 'white',
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 3, md: 6 }, position: 'relative', backgroundColor: 'white' }}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: '#1a1a1a',
          }}
        >
          <CloseIcon sx={{ fontSize: 32 }} />
        </IconButton>

        {/* Title */}
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            fontFamily: '"Impact", sans-serif',
            mb: 3,
            textTransform: 'uppercase',
          }}
        >
          CONNECT WITH ME
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: { xs: '1rem', md: '1.125rem' },
            lineHeight: 1.6,
            mb: 2,
          }}
        >
          {type === 'speaking' && 'Interested in inviting me to speak at your event? Let\'s connect!'}
          {type === 'mentorship' && 'Looking for mentorship or coaching? I\'d love to help you grow!'}
          {type === 'general' && 'Have a question or want to connect? Send me a message!'}
        </Typography>

        {/* Divider */}
        <Box sx={{ width: '100%', height: '1px', backgroundColor: '#e0e0e0', my: 3 }} />

        {/* Contact Info */}
        <Typography
          sx={{
            fontSize: { xs: '1rem', md: '1.125rem' },
            lineHeight: 1.6,
            mb: 4,
            textAlign: 'center',
            fontWeight: 700,
          }}
        >
          Please fill out this info and I'll reach out to you ASAP
        </Typography>

        {/* Success/Error Messages */}
        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 0 }}>
            Message sent successfully! I'll get back to you soon.
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 0 }}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            name="fullName"
            placeholder="Full Name"
            variant="standard"
            value={formData.fullName}
            onChange={handleChange}
            required
            sx={{
              mb: 3,
              '& .MuiInput-root': {
                fontSize: '1.125rem',
              },
              '& .MuiInput-root:before': {
                borderBottomColor: '#ccc',
              },
            }}
          />

          <TextField
            fullWidth
            name="email"
            type="email"
            placeholder="Email"
            variant="standard"
            value={formData.email}
            onChange={handleChange}
            required
            sx={{
              mb: 3,
              '& .MuiInput-root': {
                fontSize: '1.125rem',
              },
              '& .MuiInput-root:before': {
                borderBottomColor: '#ccc',
              },
            }}
          />

          <TextField
            fullWidth
            name="message"
            placeholder="Message"
            variant="standard"
            multiline
            rows={4}
            value={formData.message}
            onChange={handleChange}
            required
            sx={{
              mb: 4,
              '& .MuiInput-root': {
                fontSize: '1.125rem',
              },
              '& .MuiInput-root:before': {
                borderBottomColor: '#ccc',
              },
            }}
          />

          <Box sx={{ textAlign: 'center' }}>
            <Button
              type="submit"
              variant="text"
              disabled={loading || success}
              sx={{
                color: '#1a1a1a',
                fontWeight: 700,
                fontSize: '1.125rem',
                textDecoration: 'underline',
                textDecorationColor: '#dc143c',
                textDecorationThickness: '2px',
                textUnderlineOffset: '4px',
                p: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#dc143c',
                },
                '&:disabled': {
                  color: '#999',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : success ? 'Sent!' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;